import { useCallback, useEffect, useState } from 'react';
import { GUIDE_CHECKLIST, GUIDE_FLOWS, GUIDE_STORAGE_KEY } from './guideConfig';
import { GuideMessage, GuideTarget } from './types';

const VALID_IDS = new Set(GUIDE_CHECKLIST.map((item) => item.id));

// Drops ids left over from earlier checklist versions (renamed/removed items)
// so a stale localStorage value can never inflate the count past 100%.
const sanitize = (ids: string[]): string[] => {
  const deduped = [...new Set(ids)].filter((id) => VALID_IDS.has(id));
  return deduped;
};

const readStoredProgress = (): string[] => {
  try {
    const raw = localStorage.getItem(GUIDE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const clean = sanitize(parsed);
    if (clean.length !== parsed.length) {
      localStorage.setItem(GUIDE_STORAGE_KEY, JSON.stringify(clean));
    }
    return clean;
  } catch {
    return [];
  }
};

export const useGuideProgress = () => {
  const [completedIds, setCompletedIds] = useState<string[]>(() => readStoredProgress());
  const [target, setTarget] = useState<GuideTarget | null>(null);

  const markComplete = useCallback((itemId: string) => {
    if (!VALID_IDS.has(itemId)) return;
    setCompletedIds((prev) => {
      if (prev.includes(itemId)) return prev;
      const next = [...prev, itemId];
      localStorage.setItem(GUIDE_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    const handler = (event: MessageEvent<GuideMessage>) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== 'tf-full-guide') return;
      if (data.type === 'progress') {
        markComplete(data.itemId);
      }
      if (data.type === 'close') {
        setTarget(null);
      }
      if (data.type === 'finish') {
        const flowIndex = GUIDE_FLOWS.findIndex((flow) => flow.id === data.flow);
        const nextFlow = GUIDE_FLOWS[flowIndex + 1];
        setTarget(nextFlow ? { flow: nextFlow.id, step: 0 } : null);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [markComplete]);

  return { completedIds, markComplete, target, setTarget };
};
